import { forwardRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import { ProductDescriptorContext, useProductDescriptorContext } from "~/domains/product/ProductContext";
import { Price } from "~/domains/pricing/components/Price/v3/Price";
import { Product } from "~/domains/product/Product";
import { fakeDescriptorFromProduct, isProductPMA } from "~/domains/product/utils/productDescriptorUtils";
import { getPackagingLabelAtLineItem } from "~/features/cart/cart/utils/cartUtils";
import {
  getPriceReferenceLabelKey,
  isPriceReferenceParamVisible,
} from "~/features/cart/cart/components/LineItemStaticParams";
import type { QuoteLineMO } from "~/features/orders/common/components/v2/models";
import { translateWarehouseName } from "~/utils/language/translationUtils";
import { QuoteLineSummary } from "../common/components/v2/OrderAndQuoteSharedStyles";
import { Param, TooltipParam } from "../common/components/v2/Params";
import { QuoteLineMessages } from "../common/components/v2/QuoteTable";
import Tag from "~/common/components/Tag/Tag";
import { Box, Stack, Typography } from "~/common/mui";
import { sumParts } from "../common/components/v2/QuoteTable/QuotePartsTable";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { isKITKITProduct, type ProductData } from "~/domains/product/types/ProductData";
import { userIsPublic, userIsRegistered } from "~/domains/user/utils/roles";

interface AlternativeProductHeaderProps {
  quoteLine: QuoteLineMO;
  hidden?: boolean;
  variant?: "main" | "alternative";
  alternative?: boolean;
}

interface BaseParamProps extends DivAttributes {
  product: ProductData;
  variant?: "vertical" | "horizontal" | "search-page";
  "data-test-id"?: string;
  children?: never;
}

export const ProductHeader = forwardRef<HTMLDivElement, AlternativeProductHeaderProps>(
  ({ quoteLine, variant = "main", alternative }, ref) => {
    const { t } = useTranslation();
    const productDescriptor = fakeDescriptorFromProduct(quoteLine.product);

    const packagingLabel = getPackagingLabelAtLineItem({
      t,
      packSize: quoteLine.packSize,
      packCode: quoteLine.packCode,
      quantity: quoteLine.parts.reduce((acc, part) => acc + part.confirmedQuantity, 0),
      productDescriptor,
    });

    const isSchaefflerIdVisible = (product: ProductDescriptor) =>
      !userIsPublic() && !userIsRegistered() && !isProductPMA(product);

    const SchaefflerIdParameter = ({
      productDescriptor: external,
    }: Omit<BaseParamProps, "product"> & { productDescriptor?: ProductDescriptor }) => {
      const { t } = useTranslation();
      const productDescriptor = useProductDescriptorContext({ productDescriptor: external });
      if (!isSchaefflerIdVisible(productDescriptor) || isKITKITProduct(productDescriptor.base)) {
        return null;
      }

      return (
        <Param label={t("orderDetails.Schaeffler.id.title")} value={productDescriptor.base.sapMaterialNumber}></Param>
      );
    };

    const title = (suffix: string) => t(`quoteDetailsPage.table.header.${suffix}`);

    const { header } = sumParts({ quoteLine, variant, t });

    return (
      <ProductDescriptorContext value={productDescriptor}>
        <QuoteLineSummary ref={ref}>
          <Param
            label={title("product")}
            value={
              <Product.DesignationContainer>
                <Product.PreferredIndicator />
                <Product.DesignationV2 variant="bodyLarge" fontWeight="bold" color="textPrimary" />
                {alternative && <Tag>{t("page.pdp.phaseinout.alternative.tag")}</Tag>}
              </Product.DesignationContainer>
            }
            footer={<Product.ShortDescriptionV2 variant="bodySmall" color="textSecondary" />}
          />

          <TooltipParam
            label={t("quoteListPage.table.header.confirmedQuantity")}
            value={t("common.of.text.with.values", {
              firstValue: header.confirmedQuantity,
              secondValue: header.requestedQuantity,
            })}
            tooltipContent={
              <Stack gap={0.5}>
                <Param label={t("quoteListPage.table.header.confirmedQuantity")} value={header.confirmedQuantity} />
                <Param label={t("quoteListPage.table.header.requestedQuantity")} value={header.requestedQuantity} />
              </Stack>
            }
          />

          <TooltipParam
            label={t("quoteListPage.table.header.estimatedDeliveryDate")}
            value={header.estimatedDeliveryDate ? header.estimatedDeliveryDate : "-"}
            footer={header.requestedDeliveryDate ? header.requestedDeliveryDate : "-"}
            tooltipContent={
              <Stack gap={0.5}>
                <Param
                  label={t("quoteListPage.table.header.estimatedDeliveryDate")}
                  value={header.estimatedDeliveryDate}
                />
                <Param label={t("quoteListPage.table.header.reqDeliveryDate")} value={header.requestedDeliveryDate} />
              </Stack>
            }
          />

          {header.price && (
            <Param
              label={t("quoteDetailsPage.table.header.price")}
              value={
                <Typography variant="bodyLarge" color="textPrimary">
                  <Price price={header.price} zeroText={t("product.priceOnRequest")} surchargeEnabled />
                </Typography>
              }
              footer={
                <Price.NetPriceUnit color="textSecondary" price={header.price} variant="short" surchargeEnabled />
              }
            />
          )}
          <Param
            label={title("total")}
            value={
              <Typography variant="bodyLarge" color="textPrimary">
                <Price as="b" price={header.totalPrice} surchargeEnabled />
              </Typography>
            }
            footer={<Price.TotalPriceUnit price={header.totalPrice} variant="short" surchargeEnabled={false} />}
          />

          <SchaefflerIdParameter />
          <Param
            label={t("cartPage.table.header.packaging.title")}
            value={packagingLabel}
            footer={translateWarehouseName(t, quoteLine.warehouseName, true)}
          />
          {renderPriceReferenceParam(quoteLine)}
        </QuoteLineSummary>
        <Box paddingLeft={2}>
          <QuoteLineMessages entry={quoteLine} />
        </Box>
      </ProductDescriptorContext>
    );
  },
);

function renderPriceReferenceParam(quoteLine: QuoteLineMO) {
  if (!isPriceReferenceParamVisible() || !quoteLine.priceReference) {
    return null;
  }
  return (
    <Param
      label={<Trans i18nKey={getPriceReferenceLabelKey()!} />}
      value={[quoteLine.priceReference, quoteLine.priceReferenceDescription].filter(Boolean).join("-")}
    />
  );
}
